import { useEffect } from 'react';

export function useFocusTrap(
  containerRef: React.RefObject<HTMLElement>,
  active = true,
  autoFocus = false
) {
  useEffect(() => {
    if (!active) return;

    const focusableSelectors = [
      'a[href]',
      'button:not([disabled])',
      'textarea:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ];

    const container = containerRef.current;
    if (!container) return;

    const getFocusable = () =>
      Array.from(container.querySelectorAll<HTMLElement>(focusableSelectors.join(','))).filter(
        (el) =>
          !el.closest('.disabled') &&
          !el.closest('.unclickable') &&
          !el.hasAttribute('disabled') &&
          el.tabIndex !== -1
      );

    const focusableElements = getFocusable();

    if (autoFocus && focusableElements.length > 0) {
      focusableElements[0].focus();
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      const elements = getFocusable();
      if (elements.length === 0) return;

      const first = elements[0];
      const last = elements[elements.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    const handleInitialTab = (e: KeyboardEvent) => {
      if (e.key === 'Tab' && container && !container.contains(document.activeElement)) {
        const focusables = getFocusable();
        if (focusables.length > 0) {
          e.preventDefault();
          focusables[0].focus();
        }
      }
    };

    container.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keydown', handleInitialTab, true); // zachytit dřív než ho browser zpracuje

    return () => {
      container.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keydown', handleInitialTab, true);
    };
  }, [containerRef, active, autoFocus]);
}
